"use client";

import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { Flex, Heading, useThemeSlot } from "@huub-web/ui";
import { Illustration } from "@huub-web/shared";
import type { IllustrationName } from "@huub-web/shared/types/IllustrationType";
import type { ThemeSlotPropsMap } from "@huub-web/ui/contexts/ThemeSlotContext";

type Props = ThemeSlotPropsMap["pageHeading"];

const SCOPE_ILLUSTRATION_MAP: Record<string, IllustrationName> = {
  advice: "certificate",
  documentsOutgoing: "clipboard",
  documentsIncoming: "clipboard",
  interestRates: "interest",
  maximumMortgage: "calculate",
  orientation: "houseAnalysis",
};

/**
 * Default page-heading scope visual — the per-scope illustration. Used when
 * the theme does not register a `scopeVisual` slot.
 */
const DefaultHeadingVisual: FC<ThemeSlotPropsMap["scopeVisual"]> = ({
  scope,
  alt,
}) => {
  const illustrationName = SCOPE_ILLUSTRATION_MAP[scope] ?? "default";

  return <Illustration name={illustrationName} alt={alt} size="s" />;
};

export const DefaultPageHeading: FC<Props> = ({
  scope,
  title,
  description,
}) => {
  const { t } = useTranslation();
  const ScopeVisual = useThemeSlot({
    key: "scopeVisual",
    defaultComponent: DefaultHeadingVisual,
  });

  return (
    <>
      <Flex justify="center">
        <ScopeVisual scope={scope} size={100} alt={t(`${scope}Heading.alt`)} />
      </Flex>
      <Heading
        variant="main"
        title={title ?? t(`${scope}Heading.title`)}
        description={description ?? t(`${scope}Heading.description`)}
      />
    </>
  );
};
